/**
 * Public Campus Service
 * 
 * Handles API calls for public campus summary pages.
 * No authentication required - public access.
 */

import axios from 'axios';
import config from '../config';

// Create a separate axios instance for public API calls (no auth required)
const publicApi = axios.create({
  baseURL: `${config.apiUrl}/v2/public`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false // No credentials needed for public endpoints
});

// Response interceptor for error handling
publicApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    console.error(`Public API Error:`, error.response?.data || error.message);
    
    const enhancedError = {
      message: error.response?.data?.message || error.message,
      status: error.response?.status,
      isNetwork: !error.response,
      isServer: error.response?.status >= 500
    };
    
    return Promise.reject(enhancedError);
  }
);

/**
 * Get basic campus information
 */
export const getCampusInfo = async (slug) => {
  try {
    const response = await publicApi.get(`/campuses/${slug}`);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get population trends data
 */
export const getPopulationData = async (slug, driveTime = 10) => {
  try {
    const response = await publicApi.get(`/campuses/${slug}/population-data`, {
      params: { drive_time: driveTime }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get area school comparison data
 */
export const getComparisonData = async (slug, driveTime = 10) => {
  try {
    const response = await publicApi.get(`/campuses/${slug}/comparison-data`, {
      params: { drive_time: driveTime }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get enrollment projections data
 */
export const getProjectionsData = async (slug) => {
  try {
    const response = await publicApi.get(`/campuses/${slug}/projections-data`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get nearby schools for map visualization
 */
export const getNearbySchools = async (slug, driveTime = 10) => {
  try {
    const response = await publicApi.get(`/campuses/${slug}/nearby-schools`, {
      params: { drive_time: driveTime }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

const publicCampusService = {
  getCampusInfo,
  getPopulationData,
  getComparisonData,
  getProjectionsData,
  getNearbySchools
};

export default publicCampusService;

